import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { IMAGES } from "@/data/site";
import { PageHero, SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { Editable } from "@/components/Editable";
import ProjectShowcase from "@/components/ProjectShowcase";
import LiveTV from "@/components/LiveTV";
import { FlashTicker, EPaperSection, BureauFeed, OttSection } from "@/components/MediaHub";
import { Clapperboard, Monitor, Camera, Volume2, Film, ArrowUpRight } from "lucide-react";

const CAP_ICONS = [Monitor, Camera, Volume2, Film];

export default function Media() {
  const { t } = useApp();
  const md = t.mediaP;
  return (
    <div>
      <PageHero image={IMAGES.mediaHero} eyebrow={md.eyebrow} title={t.pillars.media.title} subtitle={t.pillars.media.desc}>
        <Link to="/gallery" className="oj-btn-gold" data-testid="media-gallery-btn"><Film className="w-4 h-4" /> {md.viewGallery}</Link>
        <Link to="/contact" className="oj-btn-ghost">{md.bookStudio} <ArrowUpRight className="w-4 h-4" /></Link>
      </PageHero>

      {/* Corporate Overview — Media & Film Making */}
      <Section sid="media.profile" name="Media Overview" className="oj-section bg-[#E9F5F4]/40 border-b border-teal-100" data-testid="media-profile">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-3 gap-10">
          <Reveal className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="oj-eyebrow mb-3"><Clapperboard className="w-4 h-4" /> {md.corpOverview}</div>
              <Editable as="h2" id="media.profile.title" className="oj-h2 block">{md.title}</Editable>
              <div className="mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-amber-400 to-amber-500" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="space-y-5">
              <Editable as="p" id="media.profile.p0" className="text-slate-600 leading-relaxed block">{md.p0}</Editable>
              <Editable as="p" id="media.profile.p1" className="text-slate-600 leading-relaxed block">{md.p1}</Editable>
              <div className="oj-card p-6 border-l-4 !border-l-amber-400">
                <Editable as="p" id="media.profile.p2" className="text-[#0B3B39] font-semibold leading-relaxed block">{md.p2}</Editable>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <FlashTicker />
      <LiveTV />

      <Section sid="media.cap" name="Studio Capabilities" className="oj-section">
        <div className="oj-container">
          <SectionHeader eyebrow={md.studioCap} title={md.cineTitle} subtitle={md.cineSub} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {md.cap.map(([title, desc], i) => {
              const Icon = CAP_ICONS[i];
              return (
                <Reveal key={title} delay={i * 0.08}>
                  <div className="oj-card p-6 h-full">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-teal-500/15 border border-teal-400/30 mb-4"><Icon className="w-6 h-6 text-teal-600" /></div>
                    <h4 className="font-bold text-white mb-2">{title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>

      <OttSection />

      <ProjectShowcase
        category="media_project"
        eyebrow={md.projEyebrow}
        title={md.projTitle}
        subtitle={md.projSub}
      />

      <EPaperSection />
      <BureauFeed />

      <Section sid="media.feature" name="Feature Banner" className="oj-container pb-20">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-teal-900/50 h-[420px]">
            <img src={IMAGES.mediaStudio} alt="studio" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#014845]/95 via-[#014845]/40 to-transparent" />
            <div className="absolute bottom-0 p-10 max-w-xl on-dark">
              <div className="oj-eyebrow mb-2 !text-amber-300"><Clapperboard className="w-4 h-4" /> {md.featEyebrow}</div>
              <h3 className="text-2xl font-bold text-white">{md.featTitle}</h3>
              <p className="text-teal-50/90 mt-2">{md.featDesc}</p>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
